// src/components/GroupBySelect.tsx
// Dropdown for selecting the active grouping field.

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import type { GroupOption } from '@/features/workspace/types';

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GroupBySelectProps {
  groups: GroupOption[];
  activeGroup: string | null;
  onGroupChange: (field: string | null) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GroupBySelect = observer(({ groups, activeGroup, onGroupChange }: GroupBySelectProps) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const activeLabel = activeGroup
    ? groups.find((g) => g.field === activeGroup)?.labelKey
    : null;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 300;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - 200),
      width: 200,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
          'border border-border',
          open
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )}
      >
        <LayersIcon className="h-4 w-4 flex-shrink-0" />
        <span className="hidden sm:inline">
          {activeLabel ? `${t('workspace.group.label')}: ${t(activeLabel)}` : t('workspace.group.label')}
        </span>
        <ChevronDownIcon className={cn('h-3.5 w-3.5 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('workspace.group.label')}
          style={menuStyle}
          className="z-50 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-1"
        >
          {/* None option */}
          <button
            role="menuitem"
            className={cn(
              'flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-left transition-colors',
              activeGroup === null
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground hover:bg-accent/50',
            )}
            onClick={() => {
              onGroupChange(null);
              setOpen(false);
            }}
          >
            <span className="flex-1">{t('workspace.group.none')}</span>
            {activeGroup === null && (
              <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
            )}
          </button>

          <div className="h-px bg-border my-1" />

          {groups
            .filter((g) => g.field !== 'none')
            .map((group) => {
              const isActive = activeGroup === group.field;
              return (
                <button
                  key={group.field}
                  role="menuitem"
                  className={cn(
                    'flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-left transition-colors',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-foreground hover:bg-accent/50',
                  )}
                  onClick={() => {
                    onGroupChange(group.field);
                    setOpen(false);
                  }}
                >
                  <span className="flex-1 truncate">{t(group.labelKey)}</span>
                  {isActive && (
                    <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                  )}
                </button>
              );
            })}
        </div>,
        document.body,
      )}
    </>
  );
});

GroupBySelect.displayName = 'GroupBySelect';
